import { useCallback, useEffect, useState } from 'react';

/**
 * Controla a instalacao da aplicacao.
 *
 * Tres situacoes precisam ser distinguidas, e confundi-las produz uma interface
 * que mente para o usuario:
 *
 *   1. o navegador nao suporta instalacao: nao ha o que oferecer;
 *   2. o aplicativo ja esta instalado e em uso: insistir seria ruido;
 *   3. o navegador sinalizou que a instalacao e possivel: e o unico caso em que
 *      o convite aparece.
 *
 * Ver docs/PWA.md.
 */

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  prompt: () => Promise<void>;
}

export type InstallState = 'unsupported' | 'available' | 'installed' | 'dismissed';

export interface InstallPrompt {
  state: InstallState;
  canInstall: boolean;
  promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
}

function isRunningStandalone(): boolean {
  if (typeof window === 'undefined') return false;

  const displayMode =
    window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  // O Safari em iOS nao expoe display-mode e usa uma propriedade propria.
  const iosStandalone =
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

  return displayMode || iosStandalone;
}

export function useInstallPrompt(): InstallPrompt {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [state, setState] = useState<InstallState>(() =>
    isRunningStandalone() ? 'installed' : 'unsupported',
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onBeforeInstall = (event: Event) => {
      // O navegador so oferece a instalacao se o convite padrao for suprimido.
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
      setState((current) => (current === 'installed' ? current : 'available'));
    };

    const onInstalled = () => {
      setDeferred(null);
      setState('installed');
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return 'unavailable' as const;

    await deferred.prompt();
    const { outcome } = await deferred.userChoice;

    // O evento so pode ser usado uma vez. O navegador emite outro quando julgar
    // apropriado voltar a oferecer.
    setDeferred(null);
    setState(outcome === 'accepted' ? 'installed' : 'dismissed');
    return outcome;
  }, [deferred]);

  return {
    state,
    canInstall: state === 'available' && deferred !== null,
    promptInstall,
  };
}
